import React, { useContext, useMemo } from "react";
import { useLocalization } from "@fluent/react";
import PropTypes from "prop-types";
import { DateTime } from "luxon";
import {
  getValidTransferDestinations,
  getValidTransferSources,
} from "byzantine/src/AccountUtils";
import TransferSchedule from "byzantine/src/TransferSchedule";
import { FREQUENCIES } from "byzantine/src/Recurrence";
import { Alert, DateInput, TextInput } from "@narmi/design_system";
import { ContextForm } from "cerulean";
import AmountTextInput from "../form/AmountTextInput";
import AccountContext from "../contexts/AccountContext";
import ComboAccountSelector from "../ComboAccountSelector";
import FrequencySelector from "./FrequencySelector";
import LimitsDialog from "./LimitsDialog";
import useTransferLimits from "./loans/hooks/useTransferLimits";
import LoanAmountField from "./LoanAmountField";
import UserFeaturesContext from "../contexts/UserFeaturesContext";
import InstitutionSettingsContext from "../contexts/InstitutionSettingsContext";
import utils from "../../utils";
import { useCurrentUser } from "../contexts/CurrentUserContext";
import { SudoProvider } from "../../components/SudoContext";
import SudoDialog from "../../components/SudoDialog";

export const TRANSFER_FORM_TYPES = Object.freeze({
  SIMPLE_TRANSFER__EDIT: 1,
  LOAN_PAY_BY_CARD__BANK: 2,
  LOAN_OPTIONALITY__PRESET: 3,
  LOAN_OPTIONALITY__CUSTOM: 4,
  LOAN_OPTIONALITY__PRINCIPAL: 5,
});

const DEFAULT_DISPLAY_OPTIONS = Object.freeze({
  isEditedScheduledTransfer: false,
  isLoanPaymentFlow: false,
  isLoansAsSourceAllowed: true,
  useAmountSelector: false,
  showMemoField: true,
  isAmountFieldDisabled: false,
  iotTransfersOnly: false,
});

const getDisplayOptionsForTransferFormType = (transferFormType) => {
  switch (transferFormType) {
    case TRANSFER_FORM_TYPES.SIMPLE_TRANSFER__EDIT:
      return {
        ...DEFAULT_DISPLAY_OPTIONS,
        isEditedScheduledTransfer: true,
      };
    case TRANSFER_FORM_TYPES.LOAN_PAY_BY_CARD__BANK:
      return {
        ...DEFAULT_DISPLAY_OPTIONS,
        isLoanPaymentFlow: true,
        useAmountSelector: true,
      };
    case TRANSFER_FORM_TYPES.LOAN_OPTIONALITY__PRESET:
      return {
        ...DEFAULT_DISPLAY_OPTIONS,
        isLoanPaymentFlow: true,
        isLoansAsSourceAllowed: false,
        showMemoField: false,
        isAmountFieldDisabled: true,
      };
    case TRANSFER_FORM_TYPES.LOAN_OPTIONALITY__CUSTOM:
      return {
        ...DEFAULT_DISPLAY_OPTIONS,
        isLoanPaymentFlow: true,
        isLoansAsSourceAllowed: false,
        showMemoField: false,
      };
    case TRANSFER_FORM_TYPES.LOAN_OPTIONALITY__PRINCIPAL:
      return {
        ...DEFAULT_DISPLAY_OPTIONS,
        isLoanPaymentFlow: true,
        isLoansAsSourceAllowed: false,
        showMemoField: false,
        iotTransfersOnly: true,
      };
    default:
      return DEFAULT_DISPLAY_OPTIONS;
  }
};

const SimpleTransferFields = ({
  data,
  limits = {},
  formType,
  disableAddExternalAccountLink = false,
  basename = "transfer/funds",
}) => {
  const { l10n } = useLocalization();
  const { currentUser } = useCurrentUser();
  const isBusiness = currentUser?.isBusiness();
  const { accounts } = useContext(AccountContext);
  const { ach_use_available_balance_for_scheduled_push, ach_allows_push } =
    useContext(InstitutionSettingsContext);

  const {
    internal_transfers: supportsInternalTransfers,
    enable_loans_in_transfer_flow,
    loan_payment_by_external_ach,
  } = useContext(UserFeaturesContext);

  const {
    isEditedScheduledTransfer,
    isLoanPaymentFlow,
    useAmountSelector,
    isAmountFieldDisabled,
    showMemoField: typeSupportsMemo,
    iotTransfersOnly,
    isLoansAsSourceAllowed,
  } = getDisplayOptionsForTransferFormType(formType);
  const showMemoField =
    typeSupportsMemo && !currentUser.features.transfers_disable_all_memos;

  const isRecurring = data?.frequency !== FREQUENCIES.ONCE;

  // Process limits using custom hook
  const { limitsForDisplay, immediateLimitsByTransactionType } =
    useTransferLimits(limits);

  const validateTransferAmount = (value, allFields) => {
    if (!value) {
      return l10n.getString("errorRequired");
    }
    const { from_account_id, to_account_id, frequency, date } = allFields;
    const transferAmountAsFloat = utils.parseValueAsFloat(value);
    const limitsAsFloat = {};
    Object.entries(immediateLimitsByTransactionType).forEach(([key, v]) => {
      limitsAsFloat[key] = parseFloat(v);
    });
    const schedule = new TransferSchedule({
      amount: transferAmountAsFloat,
      from_account_id,
      to_account_id,
      frequency,
      start_date: DateTime.fromFormat(date, "M/d/yyyy").toFormat("yyyy-MM-dd"),
      limitsAsFloat,
      ach_use_available_balance_for_scheduled_push,
      accounts,
      useBusinessVerbiage: isBusiness,
    });
    return schedule.validateTransferAmount();
  };

  const amountField = useAmountSelector ? (
    <div className="margin--bottom--l">
      <LoanAmountField data={data} validateAmount={validateTransferAmount} />
    </div>
  ) : (
    <ContextForm.Field required validate={validateTransferAmount}>
      <AmountTextInput
        field="amount"
        label={l10n.getString("label-amount")}
        disabled={isAmountFieldDisabled}
      />
    </ContextForm.Field>
  );

  const recurringSubscript = isEditedScheduledTransfer
    ? "recurring-edit"
    : "recurring-new";
  const dateLabel = l10n.getString(
    `label-date-${isRecurring ? recurringSubscript : "once"}`
  );

  const showAddExternalAccountLinkInSourceDropdown = useMemo(() => {
    if (disableAddExternalAccountLink) {
      return false;
    }
    if (!data?.to_account_id) {
      return true;
    }

    const destinationAccount = accounts.find(
      (a) => a.id === data.to_account_id
    );

    return destinationAccount?.isValidAchTransferDestination() || false;
  }, [accounts, data?.to_account_id, disableAddExternalAccountLink]);

  const showAddExternalAccountLinkInDestinationDropdown = useMemo(() => {
    if (disableAddExternalAccountLink) {
      return false;
    }
    if (!data?.from_account_id) {
      return true;
    }

    const sourceAccount = accounts.find((a) => a.id === data.from_account_id);

    return sourceAccount?.isValidAchTransferSource() || false;
  }, [accounts, data?.from_account_id, disableAddExternalAccountLink]);

  const validTransferSources = getValidTransferSources(
    accounts,
    data?.to_account_id,
    supportsInternalTransfers,
    loan_payment_by_external_ach,
    isLoansAsSourceAllowed
  );

  return (
    <div className="transfer-fields">
      <SudoProvider basename={basename}>
        <ContextForm.Field required>
          <ComboAccountSelector
            field="from_account_id"
            label={
              isLoanPaymentFlow
                ? l10n.getString("label-account")
                : l10n.getString("label-from")
            }
            accounts={validTransferSources}
            showAddExternalAccountLink={
              showAddExternalAccountLinkInSourceDropdown
            }
            basename={basename}
          />
        </ContextForm.Field>
        {isLoanPaymentFlow && validTransferSources.length === 0 && (
          <div className="margin--bottom--l">
            <Alert isActive isDismissable={false} kind="info" icon="info">
              {l10n.getString("loan-payment-no-eligible-source-accounts")}
            </Alert>
          </div>
        )}
        {!isLoanPaymentFlow && (
          <ContextForm.Field required>
            <ComboAccountSelector
              field="to_account_id"
              label={l10n.getString("label-to")}
              accounts={getValidTransferDestinations(
                accounts,
                ach_allows_push,
                data.from_account_id,
                supportsInternalTransfers,
                enable_loans_in_transfer_flow
              )}
              isDestination={true}
              showAddExternalAccountLink={
                showAddExternalAccountLinkInDestinationDropdown
              }
              basename={basename}
            />
          </ContextForm.Field>
        )}
        {amountField}
        {!iotTransfersOnly && (
          <>
            <ContextForm.Field required>
              <FrequencySelector
                field="frequency"
                label={l10n.getString("label-frequency")}
              />
            </ContextForm.Field>
            <ContextForm.Field required>
              <DateInput
                minDate={
                  !isRecurring && !isEditedScheduledTransfer
                    ? "today"
                    : DateTime.now().plus({ days: 1 }).toFormat("MM/dd/yyyy")
                }
                dateFormat={"m/d/Y"}
                altInput={true}
                altFormat={"m/d/Y"}
                field="date"
                label={dateLabel}
                useIsoOnChange={false}
              />
            </ContextForm.Field>
          </>
        )}
        {showMemoField && (
          <ContextForm.Field>
            <TextInput
              field="memo"
              label={l10n.getString("label-memo-optional")}
              maxLength={128}
            />
          </ContextForm.Field>
        )}
        <LimitsDialog limitsForDisplay={limitsForDisplay} />
        <SudoDialog />
      </SudoProvider>
    </div>
  );
};

SimpleTransferFields.propTypes = {
  data: PropTypes.object,
  limits: PropTypes.object,
  formType: PropTypes.number,
  disableAddExternalAccountLink: PropTypes.bool,
  basename: PropTypes.string,
};

export default SimpleTransferFields;
